import "../../../components/common/Tables/DataTable.css"
import React, {useMemo, useState} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import RootState from "../../../redux/states/root.state";
import { UserGroup } from "../../../redux/models/userGroup.model";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { PencilIcon, TrashIcon } from "../../../components/common/Icons/Icons";

type Order = 'asc' | 'desc';
const headers = [
  {id:'name', label:'Name'},
  {id:'description', label:'Description'},
  {id:'permissions_count', label:'Permissions', align:'center'},
  {id:'users_count', label:'Users', align:'center'},
  {id:'edit', label:'Edit', align:'center'},
  {id:'delete', label:'Delete', align:'center'},
];

// Row actions pinned to the right edge (px, fits each heading)
const actionWidths: Record<string, number> = {
  edit: 60,
  delete: 68,
};

// Sorted on this page only
const compare = (a: UserGroup, b: UserGroup, column: string) => {
  const byName = (a?.name ?? '').localeCompare(b?.name ?? '');
  switch (column) {
    case 'permissions_count':
      return (a?.permissions_count ?? 0) - (b?.permissions_count ?? 0) || byName;
    case 'users_count':
      return (a?.users_count ?? 0) - (b?.users_count ?? 0) || byName;
    case 'description':
      return (a?.description ?? '').localeCompare(b?.description ?? '') || byName;
    default:
      return byName;
  }
};

interface TableProps {
  setEditOpen: any;
  setDeleteOpen: any;
  setUserGroupToModify: any;
}

const UserGroupsTable: React.FC<TableProps> = ({setEditOpen, setDeleteOpen, setUserGroupToModify}) => {
  const navigate = useNavigate();
  const {data:userGroups, loading:isLoading, meta} = useSelector((state: RootState) => state.userGroups);
  const [searchParams, setSearchParams] = useSearchParams();
  const {canAccess:canDeleteUserGroup} = usePermissionCheck(['delete_user_groups']);
  const {canAccess:canEditUserGroup} = usePermissionCheck(['update_user_groups']);

  // Table Sort
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('name');

  const rows = useMemo(() => {
    const direction = order === 'asc' ? 1 : -1;
    return [...(userGroups ?? [])].sort((a: UserGroup, b: UserGroup) => compare(a, b, orderBy) * direction);
  }, [userGroups, orderBy, order]);

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(header_id);
  };

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.total_pages ?? 1;
  const total = meta?.total ?? 0;

  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  }

  // Edit / Delete only for those allowed
  const visibleHeaders = headers.filter((header)=>
    (header.id !== 'edit' || canEditUserGroup) && (header.id !== 'delete' || canDeleteUserGroup)
  );
  const pinned = visibleHeaders.map((header)=>header.id).filter((id)=>id in actionWidths);

  const pinStyle = (id: string) => {
    const index = pinned.indexOf(id);
    if (index < 0) return undefined;
    const right = pinned.slice(index + 1).reduce((sum, next) => sum + actionWidths[next], 0);
    return {'--dt-right': `${right}px`, '--dt-w': `${actionWidths[id]}px`} as React.CSSProperties;
  };

  const cellClass = (header: typeof headers[number]) => [
    header.align ? 'dt-center' : '',
    pinned.includes(header.id) ? 'dt-pin' : '',
    pinned[0] === header.id ? 'dt-pin-start' : '',
  ].filter(Boolean).join(' ') || undefined;

  const openModal = (userGroup: UserGroup, modal: 'edit' | 'delete') => {
    setUserGroupToModify(userGroup);
    if (modal === 'edit') setEditOpen(true);
    else setDeleteOpen(true);
  };

  const renderCell = (id: string, userGroup: UserGroup) => {
    switch (id) {
      case 'name':
        return (
          <a
            href={`/user-groups/${userGroup?.id}`}
            className="dt-name"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate(`/user-groups/${userGroup?.id}`);
            }}
          >
            {userGroup?.name || "-"}
          </a>
        );

      case 'description':
        return userGroup?.description
          ? <span className="dt-body ug-description" title={userGroup.description}>{userGroup.description}</span>
          : <span className="dt-na">—</span>;

      case 'permissions_count':
        return <span className="dt-num">{(userGroup?.permissions_count ?? 0).toLocaleString()}</span>;

      case 'users_count':
        return <span className="dt-num">{(userGroup?.users_count ?? 0).toLocaleString()}</span>;

      case 'edit':
        return (
          <Tooltip title="Edit user group" arrow>
            <button type="button" className="dt-act" onClick={()=>openModal(userGroup, 'edit')} aria-label="Edit user group">
              <PencilIcon/>
            </button>
          </Tooltip>
        );

      case 'delete':
        return (
          <Tooltip title="Delete user group" arrow>
            <button type="button" className="dt-act danger" onClick={()=>openModal(userGroup, 'delete')} aria-label="Delete user group">
              <TrashIcon/>
            </button>
          </Tooltip>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dt">
      <div className="dt-scroll">
        {isLoading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading user groups"/></div>
        ) : rows.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {visibleHeaders.map((header)=>(
                  <th
                    key={header.id}
                    scope="col"
                    className={cellClass(header)}
                    style={pinStyle(header.id)}
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {header.id !== 'delete' && header.id !== 'edit' ? (
                      <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={()=>handleRequestSort(header.id)}/>
                    ) : header.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((userGroup: UserGroup, i: number)=>(
                <tr key={userGroup?.id ?? i}>
                  {visibleHeaders.map((header)=>(
                    <td key={header.id} className={cellClass(header)} style={pinStyle(header.id)}>
                      {renderCell(header.id, userGroup)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No User Groups Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={userGroups?.length ?? 0}
        total={total}
        noun={total === 1 ? 'User Group' : 'User Groups'}
        page={page}
        totalPages={totalPages}
        loading={isLoading}
        onPage={goToPage}
      />
    </div>
  );
};

export default UserGroupsTable;
